<template lang="pug">
#other-office-connection-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 他事業所連携情報修正

    .box-body.register-body
      FormInput(v-model="officeName" key-name="事業所名" name="office-name" type="text" placeholder="事業所名"
        :validation-rules="{ required: true, max: 100 }" :maxlength="100" required)
      FormInput(v-model="staffName" key-name="担当者名" name="staff-name" type="text" placeholder="担当者名"
        :validation-rules="{ required: true, max: 100 }" :maxlength="100" required)

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoTop" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="連携情報を更新しますか？" @ok="updateOtherOfficeConnection")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoTop")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore, editOtherOfficeConnectionStore } from "@/store/store";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import { Url } from "@/commons/constants/Url";
import SystemError from "@/commons/models/SystemError";

import OtherOfficeConnectionsService, { OtherOfficeConnectionGetModel, OtherOfficeConnectionPatchModel } from "@/commons/services/OtherOfficeConnections";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";

/**
 * ### 部屋更新画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormInput
  },
  $_veeValidate: { validator: "new" }
})
export default class RoomEdit extends Vue {
  /** 他事業所連携管理サービス */
  @Inject("OtherOfficeConnectionsService") otherOfficeConnectionsService!: OtherOfficeConnectionsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 事業所名 */
  officeName: string = "";
  /** 担当者名 */
  staffName: string = "";

  /** メールアドレス */
  mailaddress: string = "";
  /** 更新日時 */
  updated: number | null = null;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 無理矢理遷移してきた場合、他事業所のメールアドレスが空なので404へ遷移させる
    if (!editOtherOfficeConnectionStore.mailaddress) {
      this.$router.push(Url.NotFound);
      return;
    }

    const connection: OtherOfficeConnectionGetModel = (await this.getOtherUserConnection()) as OtherOfficeConnectionGetModel;
    this.officeName = connection.officeName;
    this.staffName = connection.staffName;
    this.mailaddress = connection.mailaddress;
    this.updated = connection.updated;

    this.isBusy = false;

    return;
  }

  public async getOtherUserConnection(): Promise<OtherOfficeConnectionGetModel | undefined> {
    const mailaddress: string = editOtherOfficeConnectionStore.mailaddress;
    const response: Array<OtherOfficeConnectionGetModel> = await this.otherOfficeConnectionsService.gets(userStore);
    for (const connection of response) {
      if (connection.mailaddress == mailaddress) {
        return connection;
      }
    }
    // 遷移タイミングで対象の情報が削除されている場合があるので、その場合は404エラーとして扱う
    this.$router.push(Url.NotFound);
  }

  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  public async updateOtherOfficeConnection(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";
    try {
      const requestBody: OtherOfficeConnectionPatchModel = this.createRequestBody();
      await this.otherOfficeConnectionsService.patch(userStore, requestBody);

      // store reset
      editOtherOfficeConnectionStore.setMailaddress({ mailaddress: "" });

      this.showUpdateSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }
    return;
  }

  public createRequestBody(): OtherOfficeConnectionPatchModel {
    const requestBody: OtherOfficeConnectionPatchModel = {
      mailaddress: this.mailaddress,
      officeName: this.officeName,
      staffName: this.staffName,
      updated: this.updated as number
    };
    return requestBody;
  }

  public gotoTop(): void {
    this.$router.push(PcUrl.HomeForOtherOffice);
    return;
  }
}
</script>

<style lang="scss" scoped>
#other-office-connection-edit {
  max-width: 660px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
